export const VISUAL_MODEL_KIND = {
  NONE: 0,
  TABLE: 1,
  VIEW: 2,
};

export const VISUAL_EXECUTION_MODE = {
  REALTIME: 1,
  CLIENT: 2,
  SERVER: 3,
  SERVER_WORKSPACE: 4,
};

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const isTableVisualModelKind = (modelKind) =>
  normalizeNumber(modelKind) === VISUAL_MODEL_KIND.TABLE;

export const isForcedVisualExecutionMode = (_modelKind) =>
  false;

export const supportsRealtimeVisualExecutionMode = (modelKind, supportsRealTime = true) =>
  isTableVisualModelKind(modelKind) && !!supportsRealTime;

export const getDefaultVisualExecutionMode = (modelKind, supportsRealTime = true) =>
  supportsRealtimeVisualExecutionMode(modelKind, supportsRealTime)
    ? VISUAL_EXECUTION_MODE.REALTIME
    : VISUAL_EXECUTION_MODE.SERVER;

export const isVisualExecutionModeAvailable = (mode, modelKind, supportsRealTime = true) => {
  const normalizedMode = normalizeNumber(mode) === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
    ? VISUAL_EXECUTION_MODE.SERVER
    : normalizeNumber(mode);
  const normalizedModelKind = normalizeNumber(modelKind);

  if (!normalizedModelKind) {
    return false;
  }

  if (normalizedMode === VISUAL_EXECUTION_MODE.REALTIME) {
    return supportsRealtimeVisualExecutionMode(normalizedModelKind, supportsRealTime);
  }

  if (
    normalizedMode === VISUAL_EXECUTION_MODE.CLIENT
    || normalizedMode === VISUAL_EXECUTION_MODE.SERVER
    || normalizedMode === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
  ) {
    return normalizedModelKind === VISUAL_MODEL_KIND.TABLE || normalizedModelKind === VISUAL_MODEL_KIND.VIEW;
  }

  return false;
};

export const normalizeVisualExecutionMode = (modelKind, executionMode, supportsRealTime = true) => {
  const normalizedMode = normalizeNumber(executionMode) === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
    ? VISUAL_EXECUTION_MODE.SERVER
    : normalizeNumber(executionMode);

  if (isVisualExecutionModeAvailable(normalizedMode, modelKind, supportsRealTime)) {
    return normalizedMode;
  }

  return getDefaultVisualExecutionMode(modelKind, supportsRealTime);
};

export const resolveVisualRuntimeExecutionMode = (executionMode, isViewMode = false) => {
  const normalizedMode = normalizeNumber(executionMode);

  if (
    normalizedMode === VISUAL_EXECUTION_MODE.SERVER
    || normalizedMode === VISUAL_EXECUTION_MODE.SERVER_WORKSPACE
  ) {
    return isViewMode ? VISUAL_EXECUTION_MODE.SERVER : VISUAL_EXECUTION_MODE.SERVER_WORKSPACE;
  }

  return normalizedMode;
};

export const buildVisualExecutionModeOptions = (modelKind, supportsRealTime = true) => {
  const forced = isForcedVisualExecutionMode(modelKind);

  return [
    supportsRealtimeVisualExecutionMode(modelKind, supportsRealTime) ? {
      Text: "RealTime",
      Value: VISUAL_EXECUTION_MODE.REALTIME,
      Disabled: forced || !isVisualExecutionModeAvailable(VISUAL_EXECUTION_MODE.REALTIME, modelKind, supportsRealTime),
    } : null,
    {
      Text: "Server",
      Value: VISUAL_EXECUTION_MODE.SERVER,
      Disabled: !isVisualExecutionModeAvailable(VISUAL_EXECUTION_MODE.SERVER, modelKind, supportsRealTime),
    },
    {
      Text: "Client",
      Value: VISUAL_EXECUTION_MODE.CLIENT,
      Disabled: !isVisualExecutionModeAvailable(VISUAL_EXECUTION_MODE.CLIENT, modelKind, supportsRealTime),
    },
  ].filter(Boolean);
};

export const getVisualExecutionModeLabel = (executionMode) => {
  switch (normalizeNumber(executionMode)) {
    case VISUAL_EXECUTION_MODE.CLIENT:
      return "Client";
    case VISUAL_EXECUTION_MODE.SERVER:
    case VISUAL_EXECUTION_MODE.SERVER_WORKSPACE:
      return "Server";
    case VISUAL_EXECUTION_MODE.REALTIME:
    default:
      return "RealTime";
  }
};
